import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiFetch, ApiError } from '../lib/api';

/**
 * Auth provider. The browser holds no Supabase keys or tokens — the session
 * lives in an HttpOnly cookie set by the Worker. This provider's only job is
 * to keep React state in sync with what /api/auth/me reports.
 */

export interface User {
  id: string;
  email?: string;
  /** Application role from the `app_users` table (e.g. 'admin', 'member'). */
  role?: string | null;
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Email the user a magic link to sign in. Always resolves (no enumeration). */
  sendMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadUser() {
    try {
      const { user } = await apiFetch<{ user: User }>('/api/auth/me');
      setUser(user);
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        // Network or unexpected error — surface so the user knows something's off.
        console.error('Failed to load user', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Supabase's default ConfirmationURL verify flow redirects to the site root
    // with session tokens in the URL hash (#access_token=...&refresh_token=...).
    // Exchange them for an HttpOnly cookie before loading the user.
    const hash = window.location.hash;
    if (hash.includes('access_token=')) {
      const params = new URLSearchParams(hash.slice(1));
      const access_token = params.get('access_token');
      const refresh_token = params.get('refresh_token');
      const expires_in = Number(params.get('expires_in') ?? '3600');
      if (access_token && refresh_token) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        apiFetch('/api/auth/session', {
          method: 'POST',
          body: JSON.stringify({ access_token, refresh_token, expires_in }),
        })
          .then(() => loadUser())
          .catch(() => setLoading(false));
        return;
      }
    }
    loadUser();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      async sendMagicLink(email) {
        await apiFetch('/api/auth/magic-link', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
      },
      async signOut() {
        await apiFetch('/api/auth/logout', { method: 'POST' });
        setUser(null);
      },
      refresh: loadUser,
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
